import { useId, useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ChevronDown, ChevronRight, MoreHorizontal, Plus } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { LoadingButton } from '@/components/loading-button'
import { StatusPill } from '@/components/status-pill'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuList,
  PickerMenuTrigger,
} from '@/components/picker-menu'
import { SearchSelect } from '@/components/search-select'
import { formatApiErrorMessage } from '@/lib/api-error'
import { formatMoney } from '@/lib/utils'
import { shortDate } from '@/features/purchase-orders/action-card-model'
import {
  useCreateOwedItem,
  useOwedItems,
  useOwedKinds,
  useRescheduleOwedItem,
  useSettleOwedItem,
} from './api'
import {
  isWaiting,
  kindFitsScope,
  owedStatusPill,
  nextStepText,
  owedTitle,
  shortDay,
  todayIso,
  waitingCounts,
} from './model'
import type { OwedItem, OwedScope } from './types'

const LINK_CLASS =
  'font-medium text-foreground underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground'

/**
 * A plain link down to an email in this record's conversation, the way the action cards'
 * source-email links open it (USA-1417). Customer emails only open from the customer thread.
 */
function EmailLink({
  scope,
  item,
  messageId,
  children,
}: {
  scope: OwedScope
  item: OwedItem
  messageId: number
  children: ReactNode
}) {
  const search = { message: String(messageId) }
  if (item.message_thread_kind === 'customer') {
    if (scope.kind !== 'customer_thread') return null
    return (
      <Link to="/customer-comms/$threadId" params={{ threadId: String(scope.threadId) }} search={search} className={LINK_CLASS}>
        {children}
      </Link>
    )
  }
  if (scope.kind === 'purchase_order') {
    return (
      <Link to="/purchase-orders/$poId" params={{ poId: scope.poUuid }} search={search} className={LINK_CLASS}>
        {children}
      </Link>
    )
  }
  if (scope.kind === 'ap_invoice') {
    return (
      <Link to="/invoices/$invoiceId" params={{ invoiceId: scope.invoiceUuid }} search={search} className={LINK_CLASS}>
        {children}
      </Link>
    )
  }
  return null
}

function DueDateForm({
  item,
  scope,
  onClose,
}: {
  item: OwedItem
  scope: OwedScope
  onClose: () => void
}) {
  const id = useId()
  const [dueOn, setDueOn] = useState(item.due_on)
  const reschedule = useRescheduleOwedItem(scope)
  return (
    <form
      className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-end"
      onSubmit={(event) => {
        event.preventDefault()
        if (!dueOn) return
        reschedule.mutate(
          { uuid: item.uuid, dueOn },
          {
            onSuccess: () => {
              toast.success(`Now due ${shortDate(dueOn)}`)
              onClose()
            },
            onError: (error) => toast.error(formatApiErrorMessage('Due date not changed', error)),
          },
        )
      }}
    >
      <div className="min-w-0 space-y-1 sm:w-44">
        <Label htmlFor={id}>New due date</Label>
        <Input id={id} type="date" value={dueOn} min={todayIso()} onChange={(event) => setDueOn(event.target.value)} autoFocus />
      </div>
      <div className="flex gap-2">
        <LoadingButton
          type="submit"
          size="sm"
          loading={reschedule.isPending}
          loadingText="Saving..."
          disabled={!dueOn || dueOn === item.due_on}
        >
          Save
        </LoadingButton>
        <Button type="button" size="sm" variant="ghost" disabled={reschedule.isPending} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

function OwedRow({
  item,
  scope,
  onSettle,
}: {
  item: OwedItem
  scope: OwedScope
  onSettle: (item: OwedItem, status: 'received' | 'waived', outcome?: 'refund' | 'credit') => void
}) {
  const [editingDue, setEditingDue] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const pick = (action: () => void) => () => {
    setMenuOpen(false)
    action()
  }
  const pill = owedStatusPill(item)
  const next = nextStepText(item)
  const party = item.party_name?.trim()
  const asked =
    item.asked_at || item.asked_message_id != null ? (
      <span>
        {item.asked_at && `Asked ${shortDay(item.asked_at)}`}
        {item.asked_at && item.asked_message_id != null && ' · '}
        {item.asked_message_id != null && (
          <EmailLink scope={scope} item={item} messageId={item.asked_message_id}>
            our email →
          </EmailLink>
        )}
      </span>
    ) : null
  const receivedIn =
    item.status === 'received' && item.settled_message_id != null ? (
      <EmailLink scope={scope} item={item} messageId={item.settled_message_id}>
        in {party ? `${party}'s` : 'their'} email →
      </EmailLink>
    ) : null

  return (
    <li className="py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
        <p className="min-w-0 flex-1 basis-48 break-words text-sm font-semibold leading-snug">
          {owedTitle(item)}
          {item.amount != null && (
            <span className="ml-1.5 font-medium tabular-nums text-muted-foreground">{formatMoney(item.amount)}</span>
          )}
        </p>
        <div className="flex shrink-0 items-center gap-1">
          <StatusPill tone={pill.tone} className="tabular-nums">
            {pill.text}
          </StatusPill>
          {isWaiting(item) && (
            <PickerMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <PickerMenuTrigger asChild>
                <Button size="icon-xs" variant="ghost" aria-label={`More for ${owedTitle(item)}`}>
                  <MoreHorizontal aria-hidden="true" />
                </Button>
              </PickerMenuTrigger>
              <PickerMenuContent title={owedTitle(item)} titleOnPhoneOnly align="end">
                <PickerMenuList>
                  {item.refund_or_credit ? (
                    <>
                      <PickerMenuItem onSelect={pick(() => onSettle(item, 'received', 'refund'))}>Refunded</PickerMenuItem>
                      <PickerMenuItem onSelect={pick(() => onSettle(item, 'received', 'credit'))}>
                        Credit on account
                      </PickerMenuItem>
                    </>
                  ) : (
                    <PickerMenuItem onSelect={pick(() => onSettle(item, 'received'))}>Received</PickerMenuItem>
                  )}
                  <PickerMenuItem onSelect={pick(() => onSettle(item, 'waived'))}>Not needed</PickerMenuItem>
                  <PickerMenuItem onSelect={pick(() => setEditingDue(true))}>Change due date</PickerMenuItem>
                </PickerMenuList>
              </PickerMenuContent>
            </PickerMenu>
          )}
        </div>
      </div>
      <p className="mt-0.5 break-words text-xs text-muted-foreground">{item.summary}</p>
      {next && <p className="mt-0.5 text-xs text-muted-foreground">{next}</p>}
      {(asked || receivedIn) && (
        <p className="mt-0.5 break-words text-xs text-muted-foreground">
          {asked}
          {asked && receivedIn && ' · '}
          {receivedIn}
        </p>
      )}
      {editingDue && isWaiting(item) && (
        <DueDateForm item={item} scope={scope} onClose={() => setEditingDue(false)} />
      )}
    </li>
  )
}

function AddOwedForm({ scope, onClose }: { scope: OwedScope; onClose: () => void }) {
  const id = useId()
  const kinds = useOwedKinds(true)
  const create = useCreateOwedItem(scope)
  const [kind, setKind] = useState('')
  const [summary, setSummary] = useState('')
  const [dueOn, setDueOn] = useState('')
  const options = (kinds.data ?? []).filter((option) => kindFitsScope(option.owed_by, scope))
  const ready = kind !== '' && summary.trim() !== ''

  return (
    <form
      className="space-y-3 border-b pb-3"
      onSubmit={(event) => {
        event.preventDefault()
        if (!ready) return
        create.mutate(
          {
            kind,
            summary: summary.trim(),
            due_on: dueOn || null,
            purchase_order_uuid: scope.kind === 'purchase_order' ? scope.poUuid : null,
            ap_invoice_uuid: scope.kind === 'ap_invoice' ? scope.invoiceUuid : null,
            customer_thread_uuid: scope.kind === 'customer_thread' ? scope.threadUuid : null,
          },
          {
            onSuccess: () => {
              toast.success('Added to Waiting on')
              onClose()
            },
            onError: (error) => toast.error(formatApiErrorMessage('Not added', error)),
          },
        )
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="min-w-0 space-y-1">
          <Label htmlFor={`${id}-kind`}>What</Label>
          <SearchSelect
            id={`${id}-kind`}
            className="h-9 w-full"
            value={kind || undefined}
            options={options.map((option) => ({ value: option.key, label: option.label }))}
            onChange={(next) => next && setKind(next)}
            disabled={kinds.isLoading}
            placeholder={kinds.isLoading ? 'Loading...' : 'Choose'}
            searchPlaceholder="Search"
          />
        </div>
        <div className="min-w-0 space-y-1">
          <Label htmlFor={`${id}-due`}>Due</Label>
          <Input
            id={`${id}-due`}
            type="date"
            value={dueOn}
            min={todayIso()}
            onChange={(event) => setDueOn(event.target.value)}
          />
        </div>
      </div>
      <div className="min-w-0 space-y-1">
        <Label htmlFor={`${id}-summary`}>Summary</Label>
        <InlineFieldInput
          id={`${id}-summary`}
          value={summary}
          maxLength={140}
          autoComplete="off"
          onChange={(event) => setSummary(event.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <LoadingButton type="submit" size="sm" loading={create.isPending} loadingText="Adding..." disabled={!ready}>
          Add
        </LoadingButton>
        <Button type="button" size="sm" variant="ghost" disabled={create.isPending} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

/**
 * "Waiting on": what the supplier, carrier or customer still owes us on this record, and what
 * already came in (USA-1423). Folded to one line until opened.
 */
export function WaitingOn({ scope, defaultOpen = false }: { scope: OwedScope; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  const [adding, setAdding] = useState(false)
  const [showClosed, setShowClosed] = useState(false)
  const listId = useId()
  const owed = useOwedItems(scope)
  const settle = useSettleOwedItem(scope)
  const items = owed.data ?? []
  const waiting = items.filter(isWaiting)
  const closed = items.filter((item) => !isWaiting(item))
  const counts = waitingCounts(items)

  const onSettle = (item: OwedItem, status: 'received' | 'waived', outcome?: 'refund' | 'credit') =>
    settle.mutate(
      { uuid: item.uuid, status, outcome },
      {
        onSuccess: () =>
          toast.success(
            outcome === 'refund'
              ? 'Refund recorded'
              : outcome === 'credit'
                ? 'Credit recorded on their account'
                : status === 'received'
                  ? `${owedTitle(item)} received`
                  : `${owedTitle(item)} not needed`,
          ),
        onError: (error) => toast.error(formatApiErrorMessage('Not saved', error)),
      },
    )

  return (
    <Card className="gap-0 py-0">
      <CardContent className="p-0">
        <div className="flex items-center gap-2 px-4 py-3">
          <h3 className="min-w-0 flex-1 text-sm">
            <button
              type="button"
              aria-expanded={open}
              aria-controls={listId}
              onClick={() => setOpen((value) => !value)}
              className="flex w-full min-w-0 items-center gap-1.5 rounded-sm text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              {open ? (
                <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              ) : (
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              )}
              <span className="font-semibold">Waiting on</span>
              {owed.isError ? (
                <span className="text-muted-foreground">{" · couldn't load"}</span>
              ) : (
                !owed.isLoading && (
                  <span className="tabular-nums text-muted-foreground">
                    {` · ${counts.waiting}`}
                    {counts.late > 0 && (
                      <span className="font-medium text-red-700 dark:text-red-400"> ({counts.late} late)</span>
                    )}
                  </span>
                )
              )}
            </button>
          </h3>
          {!adding && (
            <Button
              size="xs"
              variant="ghost"
              onClick={() => {
                setOpen(true)
                setAdding(true)
              }}
            >
              <Plus aria-hidden="true" /> Add
            </Button>
          )}
        </div>
        {open && (
          <div id={listId} className="space-y-3 border-t px-4 py-3">
            {adding && <AddOwedForm scope={scope} onClose={() => setAdding(false)} />}
            {waiting.length > 0 ? (
              <ul className="divide-y" aria-label="Waiting on">
                {waiting.map((item) => (
                  <OwedRow key={item.uuid} item={item} scope={scope} onSettle={onSettle} />
                ))}
              </ul>
            ) : (
              !adding && <p className="text-sm text-muted-foreground">{owed.isLoading ? 'Loading...' : 'Nothing owed'}</p>
            )}
            {closed.length > 0 && (
              <div className="space-y-3">
                <button
                  type="button"
                  aria-expanded={showClosed}
                  onClick={() => setShowClosed((value) => !value)}
                  className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  {showClosed ? (
                    <ChevronDown className="size-3.5" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="size-3.5" aria-hidden="true" />
                  )}
                  Done ({closed.length})
                </button>
                {showClosed && (
                  <ul className="divide-y" aria-label="Done">
                    {closed.map((item) => (
                      <OwedRow key={item.uuid} item={item} scope={scope} onSettle={onSettle} />
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
